import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const MAX_FILES = 6;
const MAX_BYTES = 5 * 1024 * 1024;

export const PhotoUpload = ({ files, setFiles, idPrefix = "photo" }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    const valid = [];
    for (const f of list) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image file.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is larger than 5 MB.`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => {
      const next = [...prev, ...valid].slice(0, MAX_FILES);
      if (prev.length + valid.length > MAX_FILES) {
        toast.error(`You can upload up to ${MAX_FILES} photos.`);
      }
      return next;
    });
  };

  const removeAt = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ves-green ${
          dragging
            ? "border-ves-green bg-ves-forest/40"
            : "border-white/20 bg-white/5 hover:border-ves-green/60"
        }`}
        data-testid={`${idPrefix}-dropzone`}
      >
        <ImagePlus className="mb-3 h-10 w-10 text-ves-green" />
        <p className="font-heading text-base font-bold text-white">
          Drag &amp; drop photos here, or tap to browse
        </p>
        <p className="mt-1 text-sm text-white/60">
          On a phone? Use your camera — up to {MAX_FILES} photos, 5 MB each.
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
        data-testid={`${idPrefix}-input`}
      />
      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6" data-testid={`${idPrefix}-previews`}>
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="relative border border-white/15">
              <img
                src={URL.createObjectURL(f)}
                alt={f.name}
                className="h-20 w-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center bg-ves-green text-ves-black"
                aria-label={`Remove ${f.name}`}
                data-testid={`${idPrefix}-remove-${i}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
